<script>
  import { untrack } from 'svelte'
  import { useForm } from '@inertiajs/svelte'
  import { liveValidate } from '../../lib/live_validation.svelte.js'
  import Drawer from '../drawer.svelte'
  import MultiSelect from '../multi_select.svelte'
  import PlaceSearch from '../place_search.svelte'
  import TextField from '../form/text_field.svelte'
  import SelectField from '../form/select_field.svelte'
  import SubmitButton from '../form/submit_button.svelte'

  // The adventure form's inline "New place" (old CMS AdventureForm "Create New"): creates a
  // place with the essentials without leaving the half-filled adventure. The rest (schedule,
  // icon, gallery, …) is edited on the place's own page afterwards.
  // The server fills in the rest (display name defaults to the name, an always-open schedule
  // in the town's time zone) and answers with the place as flash data, or its validation
  // errors in the `quick_place` error bag (PlacesController#quick_create_response).
  // `areaOptions` / `subCategoryOptions` are server-built [{ value, label }].
  let { open = $bindable(false), areaOptions = [], subCategoryOptions = [], areaId = null, oncreated } = $props()

  const blank = () => ({ name: '', display_name: '', area_id: '', sub_category_ids: [], google_id: '', latitude: '', longitude: '', visit_us_url: '', book_tour_url: '' })

  // Validated live against the real create action (Precognition, `place.*` keys).
  const form = useForm({ quick_create: true, place: blank() }).withPrecognition('post', '/places')
  const live = liveValidate(form, 'place')

  let location = $state(null)

  $effect(() => {
    if (!open) return
    untrack(() => {
      $form.place = { ...blank(), area_id: areaId ?? '' }
      $form.clearErrors()
      live.reset()
      location = null
      showApartmentFields = false
    })
  })

  const onLocation = (picked) => {
    location = picked
    $form.place.google_id = picked?.placeId ?? ''
    $form.place.latitude = picked?.lat ?? ''
    $form.place.longitude = picked?.lng ?? ''
  }

  const error = (key) => $form.errors[`place.${key}`]

  // An Apartments category makes both URLs required (Place#validate_apartment_urls); the server
  // says so on submit, and the fields stay once shown so the admin can fill them in.
  let showApartmentFields = $state(false)
  $effect(() => {
    if (error('visit_us_url') || error('book_tour_url')) showApartmentFields = true
  })

  const shownFields = ['name', 'display_name', 'area_id', 'visit_us_url', 'book_tour_url']
  const otherError = $derived(
    Object.entries($form.errors).find(([key]) => !shownFields.includes(key.replace(/^place\./, '')))?.[1]
  )

  const submit = (event) => {
    event.preventDefault()
    $form.post('/places', {
      preserveState: true,
      preserveScroll: true,
      only: ['flash'],
      errorBag: 'quick_place',
      onFlash: ({ created_place: createdPlace }) => {
        if (!createdPlace) return
        oncreated?.(createdPlace)
        open = false
      }
    })
  }
</script>

<Drawer bind:open title="New place">
  <form novalidate onsubmit={submit} class="space-y-4">
    <TextField bind:value={$form.place.name} label="Name" name="name" error={error('name')} />
    <TextField bind:value={$form.place.display_name} label="Display Name" name="display_name" help="Displayed in the app. Defaults to the name." error={error('display_name')} />
    <SelectField bind:value={$form.place.area_id} label="College Town" name="area_id" options={areaOptions} error={error('area_id')} />
    <MultiSelect bind:value={$form.place.sub_category_ids} options={subCategoryOptions} label="Categories" placeholder="Select a category…" />

    {#if showApartmentFields}
      <TextField bind:value={$form.place.visit_us_url} label="Visit Us URL" name="visit_us_url" help="URL for visitors to learn more about this apartment" error={error('visit_us_url')} />
      <TextField bind:value={$form.place.book_tour_url} label="Book Tour URL" name="book_tour_url" help="URL for visitors to book a tour of this apartment" error={error('book_tour_url')} />
    {/if}

    <fieldset class="fieldset">
      <legend class="fieldset-legend">Google place</legend>
      <PlaceSearch onselect={onLocation} />
      {#if location?.address}<p class="text-xs text-base-content/60">{location.address}</p>{/if}
    </fieldset>

    {#if otherError}<p class="text-sm text-error">{otherError}</p>{/if}

    <SubmitButton processing={$form.processing} label="Create place" />
  </form>
</Drawer>
